import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Plus } from 'lucide-react';
import { TableSearch } from '../../../../../components/form/Fields';
import CommonButton from '../../../../../components/commonButton';
import CustomTable from '../../../../../components/table/CustomTable';
import StatusChip from '../../../../../components/statusChip/StatusChip';
import DeletePopup from '../../../../../components/delete-popup';
import ClientModal from '../../../../BrokerCRM/Clients/ClientModal';
import {
    fetchClientConfig,
    createClient,
    updateClient,
    toggleClientStatus,
    deleteClient,
} from '../../../../../redux/slice/brokerClientsSlice';
import Axios, { authHeaders } from '../../../../../utils/axios';
import { formatMomentDate } from '../../../../../utils/dateHelper';
import { useToast } from '../../../../../context/TostContext';

const CLIENT_COLUMNS = [
    {
        header: 'Name',
        cell: (_, item) => <span className="fw-medium text-dark">{item.name || '—'}</span>,
    },
    {
        header: 'Mobile Number',
        cell: (_, item) => item.mobile_number || '—',
    },
    {
        header: 'Email',
        cell: (_, item) => item.email || '—',
    },
    {
        header: 'State',
        cell: (_, item) => item.state?.name || '—',
    },
    {
        header: 'City',
        cell: (_, item) => item.city?.name || '—',
    },
    {
        header: 'Status',
        cell: (_, item) => (
            <StatusChip
                bg={item.is_active ? 'success' : 'danger'}
                pill
                title={item.is_active ? 'Active' : 'Inactive'}
            />
        ),
    },
    {
        header: 'Created At',
        cell: (_, item) => formatMomentDate(item.created_at, 'DD-MM-YYYY hh:mm A'),
    },
    {
        header: 'Updated At',
        cell: (_, item) => formatMomentDate(item.updated_at, 'DD-MM-YYYY hh:mm A'),
    },
];

const Clients = ({ userId, userDetail }) => {
    const dispatch = useDispatch();
    const toast = useToast();
    const { config = {} } = useSelector((state) => state.brokerClients || {});

    // List state
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [loading, setLoading] = useState(false);
    const [dataList, setDataList] = useState([]);
    const [total, setTotal] = useState(0);

    // Modal state
    const [openModal, setOpenModal] = useState(false);
    const [selectedClient, setSelectedClient] = useState(null);
    const [isEditMode, setIsEditMode] = useState(false);
    const [isViewMode, setIsViewMode] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Delete popup state
    const [showDeletePopup, setShowDeletePopup] = useState(false);
    const [itemToDelete, setItemToDelete] = useState(null);
    const [deleting, setDeleting] = useState(false);

    // Fetch config dropdowns on mount
    useEffect(() => {
        dispatch(fetchClientConfig());
    }, [dispatch]);

    const fetchData = useCallback(async () => {
        if (!userId) return;
        setLoading(true);
        try {
            const response = await Axios.get('/broker/clients', {
                params: {
                    page: currentPage,
                    limit: pageSize,
                    search: searchTerm,
                    user_id: userId,
                },
                ...authHeaders(),
            });
            const result = response.data;
            setDataList(result?.data || result?.rows || result || []);
            setTotal(result?.meta?.total || result?.count || (result?.data || result || []).length);
        } catch (error) {
            console.error('Failed to fetch Broker CRM clients:', error);
        } finally {
            setLoading(false);
        }
    }, [userId, currentPage, pageSize, searchTerm]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // ── Modal helpers ────────────────────────────────────────

    const closeModal = () => {
        setOpenModal(false);
        setSelectedClient(null);
        setIsEditMode(false);
        setIsViewMode(false);
    };

    // ── Action handlers ──────────────────────────────────────

    const handleCreate = () => {
        setSelectedClient({ user_id: userId, user: userDetail });
        setIsEditMode(false);
        setIsViewMode(false);
        setOpenModal(true);
    };

    const handleView = (row) => {
        setSelectedClient({ ...row, user: userDetail });
        setIsViewMode(true);
        setIsEditMode(false);
        setOpenModal(true);
    };

    const handleEdit = (row) => {
        setSelectedClient({ ...row, user: userDetail });
        setIsEditMode(true);
        setIsViewMode(false);
        setOpenModal(true);
    };

    const handleToggleStatus = async (row) => {
        try {
            const res = await dispatch(toggleClientStatus(row.id));
            if (res.error) {
                toast.error(res.payload?.message || 'Failed to change client status');
            } else {
                toast.success(`Client ${row.is_active ? 'deactivated' : 'activated'} successfully`);
                const updatedClient = res.payload?.data || {};
                setDataList((prev) =>
                    prev.map((item) =>
                        item.id === row.id
                            ? {
                                ...item,
                                is_active: updatedClient.is_active !== undefined ? updatedClient.is_active : !item.is_active,
                                updated_at: updatedClient.updated_at || new Date().toISOString(),
                            }
                            : item
                    )
                );
            }
        } catch {
            toast.error('Failed to change client status');
        }
    };

    const handleDeleteConfirm = async () => {
        if (!itemToDelete) return;
        setDeleting(true);
        try {
            const res = await dispatch(deleteClient(itemToDelete));
            if (res.error) {
                toast.error(res.payload?.message || 'Failed to delete client');
            } else {
                toast.success('Client deleted successfully');
                setShowDeletePopup(false);
                setItemToDelete(null);
                fetchData();
            }
        } finally {
            setDeleting(false);
        }
    };

    const onSubmitForm = async (data) => {
        setIsSubmitting(true);
        try {
            const action = isEditMode
                ? updateClient({ id: selectedClient.id, data })
                : createClient(data);
            const res = await dispatch(action);
            if (res.error) {
                toast.error(res.payload?.message || `Failed to ${isEditMode ? 'update' : 'create'} client`);
            } else {
                toast.success(`Client ${isEditMode ? 'updated' : 'created'} successfully`);
                closeModal();
                fetchData();
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    // ── Action list ──────────────────────────────────────────

    const actionList = [
        {
            title: 'View',
            handler: handleView,
        },
        {
            title: 'Edit',
            handler: handleEdit,
        },
        {
            title: (row) => (row.is_active ? 'Deactivate' : 'Activate'),
            handler: handleToggleStatus,
        },
        {
            title: 'Delete',
            handler: (row) => { setItemToDelete(row.id); setShowDeletePopup(true); },
            isDanger: true,
        },
    ];

    // ── Render ───────────────────────────────────────────────

    const totalPages = Math.ceil(total / pageSize);
    const activePage = Math.min(currentPage, Math.max(1, totalPages));

    return (
        <div>
            {/* Toolbar */}
            <div className="d-flex align-items-center justify-content-between gap-4 mb-4">
                <TableSearch
                    value={searchTerm}
                    placeholder="Search"
                    onChange={(e) => {
                        setSearchTerm(e.target.value);
                        setCurrentPage(1);
                    }}
                    onClear={() => {
                        setSearchTerm('');
                        setCurrentPage(1);
                    }}
                />
                <CommonButton
                    icon={<Plus size={16} />}
                    variant="primary"
                    title="Add Client"
                    type="button"
                    onClick={handleCreate}
                    style={{ whiteSpace: 'nowrap' }}
                />
            </div>

            {/* Table */}
            <div className="overflow-hidden border rounded-3">
                <CustomTable
                    data={dataList}
                    columns={CLIENT_COLUMNS}
                    keyField="id"
                    actionList={actionList}
                    emptyMessage="No clients found"
                    loading={loading}
                    pagination={{
                        currentPage: activePage,
                        totalPages,
                        total,
                        onPageChange: (page) => setCurrentPage(page),
                        pageSize,
                        setPageSize: (size) => { setPageSize(size); setCurrentPage(1); },
                        setCurrentPage,
                    }}
                />
            </div>

            {/* View / Edit modal */}
            <ClientModal
                isOpen={openModal}
                onClose={closeModal}
                title={isViewMode ? 'View Client' : isEditMode ? 'Edit Client' : 'Add Client'}
                size="lg"
                className="tp-file-modal"
                clientData={selectedClient}
                isEditMode={isEditMode}
                isViewMode={isViewMode}
                isUserIdLocked={!isEditMode && !isViewMode}
                onSubmit={onSubmitForm}
                configData={config}
                isSubmitting={isSubmitting}
            />

            {/* Delete confirmation */}
            <DeletePopup
                show={showDeletePopup}
                onHide={() => {
                    setShowDeletePopup(false);
                    setItemToDelete(null);
                }}
                onConfirm={handleDeleteConfirm}
                isLoading={deleting}
                saveTitle="Delete"
            />
        </div>
    );
};

export default Clients;
